/**
 * Story Upload Monitoring Hook
 *
 * Provides structured workflow + action lifecycle instrumentation for the multi-step
 * Add Story flow. Correlates all actions under a single `UPLOAD_STORY` workflow.
 *
 * Emits (new) action event names declared in `ActionEventType` with lifecycle suffixes
 * handled by the monitoring infrastructure.
 *
 * Responsibilities:
 *  - Start workflow on first committed source selection (file(s) or URL) / upload start
 *  - Track granular actions (source select, upload, type select, metadata submit,
 *    artifact uploads, b-roll uploads, processing)
 *  - Handle success / failure / cancellation
 *  - Supply consistent metadata & context
 *  - Remain a no-op when monitoring disabled
 */

import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useCallback, useRef } from 'react';

import { ActionEventType, ActionType, WorkflowType } from '../types/enums';

// Generic loose metadata bag (must be indexable for monitoring service)
interface BaseMetadata {
  [k: string]: unknown; // ensure index signature
  storyId?: string;
  orgId?: string;
}

interface SourceSelectMeta extends BaseMetadata {
  sourceType: 'file' | 'url' | 'multi_file';
  fileCount?: number;
  totalBytes?: number;
  isUrl: boolean;
}

interface UploadMeta extends BaseMetadata {
  fileCount: number;
  totalBytes: number;
  isMultiFile: boolean;
  isUrl: boolean;
  uploadStrategy: 'aws_sdk' | 'legacy' | 'unknown';
  retryIndex?: number;
}

interface StoryTypeMeta extends BaseMetadata {
  storyType: 'raw' | 'produced';
  videoMusicStrategy: 'keep' | 'process';
}

interface MetadataSubmitMeta extends BaseMetadata {
  titleLength: number;
  language?: string;
  hasCustomTitle: boolean;
  allowLanguageSelect?: boolean;
}

interface ArtifactUploadMeta extends BaseMetadata {
  count: number;
  totalBytes: number;
  cumulativeCount: number;
  artifactKind: 'doc' | 'video';
}

interface ProcessingMeta extends BaseMetadata {
  audioPreset?: string;
  transcriptionProviders?: string[];
}

export interface StoryUploadMonitoringHook {
  isEnabled: boolean;
  workflowId: string | null;
  // Lifecycle entry points
  onSourceChosen: (m: SourceSelectMeta) => void;
  onVideoUploadStart: (m: UploadMeta) => void;
  onVideoUploadComplete: (extra?: Record<string, unknown>) => void;
  onVideoUploadError: (error: unknown, extra?: Record<string, unknown>) => void;
  onStoryTypeSelected: (m: StoryTypeMeta) => void;
  onMetadataSubmitted: (m: MetadataSubmitMeta) => void;
  onArtifactUploadStart: (m: ArtifactUploadMeta) => void;
  onArtifactUploadComplete: (
    kind: 'doc' | 'video',
    extra?: Record<string, unknown>,
  ) => void;
  onArtifactUploadFail: (
    kind: 'doc' | 'video',
    error: unknown,
    extra?: Record<string, unknown>,
  ) => void;
  onProcessingStart: (m: ProcessingMeta) => void;
  onProcessingComplete: (extra?: Record<string, unknown>) => void;
  onProcessingFail: (error: unknown, extra?: Record<string, unknown>) => void;
  onWorkflowFinalizeSuccess: (extra?: Record<string, unknown>) => void;
  onWorkflowCancel: (reason?: string) => void;
}

type ActionKey =
  | 'SOURCE'
  | 'VIDEO_UPLOAD'
  | 'ARTIFACT_DOC'
  | 'ARTIFACT_VIDEO'
  | 'PROCESSING';

interface ActiveActionRecord {
  transactionId: string;
  event: ActionEventType;
  startedAt: number;
}

export function useMonitorStoryUpload(): StoryUploadMonitoringHook {
  const rootStore = useStore();
  const videoCreator = useVideoCreatorStore();

  const isEnabled = rootStore.analyticsStore.getMonitoringConfig().enabled;

  // Refs for workflow & action correlation
  const workflowIdRef = useRef<string | null>(null);
  const workflowEndedRef = useRef(false);
  const activeActionsRef = useRef<
    Partial<Record<ActionKey, ActiveActionRecord>>
  >({});

  const ensureWorkflow = useCallback(() => {
    if (!isEnabled || workflowIdRef.current || workflowEndedRef.current) return;
    const org = videoCreator.organization;
    const orgId = org?.id;
    workflowIdRef.current = rootStore.analyticsStore.startWorkflow(
      WorkflowType.UPLOAD_STORY,
      {
        orgId,
      },
      {
        business: {
          organization: orgId
            ? {
                id: orgId,
                name: org?.title || 'unknown_org',
                slug: org?.slug,
              }
            : undefined,
          environment:
            rootStore.userIdentityStore?.environment || 'unknown_env',
        },
      },
    );
  }, [isEnabled, rootStore, videoCreator.organization]);

  const startAction = useCallback(
    (
      key: ActionKey,
      event: ActionEventType,
      metadata?: Record<string, unknown>,
    ) => {
      if (!isEnabled || workflowEndedRef.current) return null;
      // Do not restart if already active under same key
      if (activeActionsRef.current[key])
        return activeActionsRef.current[key].transactionId;
      ensureWorkflow();
      const org = videoCreator.organization;
      const transactionId = rootStore.analyticsStore.startUserAction(
        event,
        ActionType.ACTION,
        workflowIdRef.current || undefined,
        {
          business: {
            storyId:
              typeof metadata?.storyId === 'string'
                ? metadata.storyId
                : undefined,
            organization: org
              ? {
                  id: org.id,
                  name: org.title || 'unknown_org',
                  slug: org.slug,
                }
              : undefined,
            environment:
              rootStore.userIdentityStore?.environment || 'unknown_env',
          },
        },
        metadata,
      );
      if (transactionId) {
        activeActionsRef.current[key] = {
          transactionId,
          event,
          startedAt: performance.now(),
        };
      }
      return transactionId;
    },
    [ensureWorkflow, isEnabled, rootStore, videoCreator.organization],
  );

  const completeAction = useCallback(
    (key: ActionKey, extra?: Record<string, unknown>) => {
      if (!isEnabled) return;
      const active = activeActionsRef.current[key];
      if (!active) return;
      rootStore.analyticsStore.completeUserAction(active.transactionId, extra);
      delete activeActionsRef.current[key];
    },
    [isEnabled, rootStore],
  );

  const failAction = useCallback(
    (key: ActionKey, error: unknown, extra?: Record<string, unknown>) => {
      if (!isEnabled) return;
      const active = activeActionsRef.current[key];
      if (!active) return;
      rootStore.analyticsStore.failUserAction(
        active.transactionId,
        error instanceof Error ? error : String(error),
        extra,
      );
      delete activeActionsRef.current[key];
    },
    [isEnabled, rootStore],
  );

  const cancelAction = useCallback(
    (key: ActionKey, reason?: string) => {
      if (!isEnabled) return;
      const active = activeActionsRef.current[key];
      if (!active) return;
      rootStore.analyticsStore.cancelUserAction(active.transactionId, reason);
      delete activeActionsRef.current[key];
    },
    [isEnabled, rootStore],
  );

  // Public API implementations
  const onSourceChosen = useCallback(
    (m: SourceSelectMeta) => {
      if (!isEnabled) return;
      ensureWorkflow();
      // Source selection = instantaneous action (start + complete)
      const tid = rootStore.analyticsStore.startUserAction(
        ActionEventType.STORY_SOURCE_SELECT,
        ActionType.ACTION,
        workflowIdRef.current || undefined,
        {
          business: {
            storyId: m.storyId,
            organization: m.orgId
              ? { id: m.orgId, name: 'unknown_org', slug: undefined }
              : undefined,
            environment:
              rootStore.userIdentityStore?.environment || 'unknown_env',
          },
        },
        m,
      );
      if (tid) rootStore.analyticsStore.completeUserAction(tid, m);
    },
    [ensureWorkflow, isEnabled, rootStore],
  );

  const onVideoUploadStart = useCallback(
    (m: UploadMeta) => {
      startAction('VIDEO_UPLOAD', ActionEventType.STORY_VIDEO_UPLOAD, m);
    },
    [startAction],
  );

  const onVideoUploadComplete = useCallback(
    (extra?: Record<string, unknown>) => {
      completeAction('VIDEO_UPLOAD', extra);
    },
    [completeAction],
  );

  const onVideoUploadError = useCallback(
    (error: unknown, extra?: Record<string, unknown>) => {
      failAction('VIDEO_UPLOAD', error, extra);
    },
    [failAction],
  );

  const onStoryTypeSelected = useCallback(
    (m: StoryTypeMeta) => {
      if (!isEnabled) return;
      const tid = rootStore.analyticsStore.startUserAction(
        ActionEventType.STORY_TYPE_SELECT,
        ActionType.ACTION,
        workflowIdRef.current || undefined,
        {
          business: {
            storyId: m.storyId,
            environment:
              rootStore.userIdentityStore?.environment || 'unknown_env',
          },
        },
        m,
      );
      if (tid) rootStore.analyticsStore.completeUserAction(tid, m);
    },
    [isEnabled, rootStore],
  );

  const onMetadataSubmitted = useCallback(
    (m: MetadataSubmitMeta) => {
      if (!isEnabled) return;
      const tid = rootStore.analyticsStore.startUserAction(
        ActionEventType.STORY_METADATA_SUBMIT,
        ActionType.ACTION,
        workflowIdRef.current || undefined,
        {
          business: {
            storyId: m.storyId,
            environment:
              rootStore.userIdentityStore?.environment || 'unknown_env',
          },
        },
        m,
      );
      if (tid) rootStore.analyticsStore.completeUserAction(tid, m);
    },
    [isEnabled, rootStore],
  );

  const onArtifactUploadStart = useCallback(
    (m: ArtifactUploadMeta) => {
      startAction(
        m.artifactKind === 'video' ? 'ARTIFACT_VIDEO' : 'ARTIFACT_DOC',
        m.artifactKind === 'video'
          ? ActionEventType.STORY_BROLL_UPLOAD
          : ActionEventType.STORY_ARTIFACT_UPLOAD,
        m,
      );
    },
    [startAction],
  );

  const onArtifactUploadComplete = useCallback(
    (kind: 'doc' | 'video', extra?: Record<string, unknown>) => {
      completeAction(
        kind === 'video' ? 'ARTIFACT_VIDEO' : 'ARTIFACT_DOC',
        extra,
      );
    },
    [completeAction],
  );

  const onArtifactUploadFail = useCallback(
    (
      kind: 'doc' | 'video',
      error: unknown,
      extra?: Record<string, unknown>,
    ) => {
      failAction(
        kind === 'video' ? 'ARTIFACT_VIDEO' : 'ARTIFACT_DOC',
        error,
        extra,
      );
    },
    [failAction],
  );

  const onProcessingStart = useCallback(
    (m: ProcessingMeta) => {
      startAction('PROCESSING', ActionEventType.STORY_PROCESSING, m);
    },
    [startAction],
  );

  const onProcessingComplete = useCallback(
    (extra?: Record<string, unknown>) => {
      completeAction('PROCESSING', extra);
    },
    [completeAction],
  );

  const onProcessingFail = useCallback(
    (error: unknown, extra?: Record<string, unknown>) => {
      failAction('PROCESSING', error, extra);
    },
    [failAction],
  );

  const onWorkflowFinalizeSuccess = useCallback(
    (extra?: Record<string, unknown>) => {
      if (!isEnabled || !workflowIdRef.current || workflowEndedRef.current)
        return;
      rootStore.analyticsStore.completeWorkflow(
        workflowIdRef.current,
        true,
        extra,
      );
      workflowEndedRef.current = true;
    },
    [isEnabled, rootStore],
  );

  const onWorkflowCancel = useCallback(
    (reason?: string) => {
      if (!isEnabled) return;
      // Cancel all active actions first
      const possibleKeys = Object.keys(activeActionsRef.current);
      const validKeys: ActionKey[] = [];
      possibleKeys.forEach(k => {
        if (
          k === 'SOURCE' ||
          k === 'VIDEO_UPLOAD' ||
          k === 'ARTIFACT_DOC' ||
          k === 'ARTIFACT_VIDEO' ||
          k === 'PROCESSING'
        ) {
          validKeys.push(k);
        }
      });
      validKeys.forEach(key => cancelAction(key, reason));
      if (workflowIdRef.current && !workflowEndedRef.current) {
        rootStore.analyticsStore.cancelWorkflow(
          workflowIdRef.current,
          reason || 'user_closed_modal',
        );
        workflowEndedRef.current = true;
      }
    },
    [cancelAction, isEnabled, rootStore],
  );

  const api: StoryUploadMonitoringHook = {
    isEnabled,
    workflowId: workflowIdRef.current,
    onSourceChosen,
    onVideoUploadStart,
    onVideoUploadComplete,
    onVideoUploadError,
    onStoryTypeSelected,
    onMetadataSubmitted,
    onArtifactUploadStart,
    onArtifactUploadComplete,
    onArtifactUploadFail,
    onProcessingStart,
    onProcessingComplete,
    onProcessingFail,
    onWorkflowFinalizeSuccess,
    onWorkflowCancel,
  };

  return api;
}
